import {
  floatingIpMonthly,
  type FloatingIp,
  type IpFamily,
  type PriceList,
  type Server,
} from "@cloudserver/contracts";
import { useReadOnly } from "../api/users.js";
import { Link } from "@tanstack/react-router";
import {
  ArrowRightLeft,
  Link2,
  Lock,
  LockOpen,
  Pencil,
  Plus,
  Terminal,
  Trash2,
  Unlink,
  Waypoints,
} from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { ApiProblem } from "../api/client.js";
import {
  useAssignFloatingIp,
  useCreateFloatingIp,
  useDeleteFloatingIp,
  useFloatingIpProtection,
  useFloatingIps,
  useFloatingReverseDns,
  useUnassignFloatingIp,
  useUpdateFloatingIp,
} from "../api/floating-ips.js";
import { progressOf, useRunningOperations } from "../api/operations.js";
import { useCatalogue } from "../api/queries.js";
import { useServers } from "../api/servers.js";
import { DangerDialog } from "../ui/confirm.js";
import { ErrorMessage, fieldError, problemText } from "../ui/feedback.js";
import { money, relativeTime } from "../ui/format.js";
import { FieldHint } from "../ui/help.js";
import { EmptyState, PageHeader } from "../ui/layout.js";
import { Dialog, type MenuItem } from "../ui/overlay.js";
import { Alert, Badge, Button, TextArea, TextField } from "../ui/primitives.js";
import { DataTable } from "../ui/table.js";
import { useToast } from "../ui/toast.js";
import { formatLabels, parseLabels } from "./labels.js";
import { ReverseDnsDialog } from "./public-ips.js";

type T = ReturnType<typeof useTranslation>["t"];

const selectClass = "min-h-10 rounded-lg border border-line bg-surface px-3 text-sm";

/** Monthly price of a floating IP of `family` at home in `region`, formatted. */
export function floatingIpPrice(
  family: IpFamily,
  region: string,
  prices: PriceList | undefined,
  locale: string,
) {
  const amount = prices ? floatingIpMonthly({ family, region }, prices) : null;
  return amount === null || !prices ? "—" : money(String(amount), prices.currency, locale);
}

/** Why a server cannot take this address now, or null when it can. */
function serverBlocker(t: T, server: Server, zone: string | null, currentId: string | null) {
  if (zone !== null && server.zone !== zone) return t("floatingIps.why.otherZone");
  if (server.id === currentId) return t("floatingIps.why.current");
  return null;
}

function ServerSelect({
  servers,
  zone,
  currentId,
  value,
  onChange,
}: {
  servers: Server[];
  /** Only servers of this network zone can take the address; null for a new one. */
  zone: string | null;
  currentId: string | null;
  value: string;
  onChange: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="font-medium">{t("floatingIps.server")}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={selectClass}
        required
      >
        <option value="">{t("floatingIps.chooseServer")}</option>
        {servers.map((s) => {
          const why = serverBlocker(t, s, zone, currentId);
          return (
            <option key={s.id} value={s.id} disabled={why !== null}>
              {s.name} ({s.region}){why ? ` — ${why}` : ""}
            </option>
          );
        })}
      </select>
      <span className="text-xs text-muted">{t("floatingIps.serverHint")}</span>
    </label>
  );
}

/** IPv4 or IPv6, each with its price at home in `region` (both cost money). */
export function FloatingFamilyChoice({
  family,
  onChange,
  region,
  prices,
}: {
  family: IpFamily;
  onChange: (family: IpFamily) => void;
  region: string;
  prices: PriceList | undefined;
}) {
  const { t, i18n } = useTranslation();
  return (
    <fieldset className="flex flex-col gap-2 text-sm">
      <legend className="mb-1 font-medium">{t("floatingIps.family")}</legend>
      {(["ipv4", "ipv6"] as const).map((f) => (
        <label
          key={f}
          className="flex cursor-pointer items-start gap-2 rounded-lg border border-line px-3 py-2 hover:bg-surface-2"
        >
          <input
            type="radio"
            name="family"
            checked={family === f}
            onChange={() => onChange(f)}
            className="mt-0.5 size-4 accent-accent"
          />
          <span className="flex flex-col">
            <span className="font-medium">{f === "ipv4" ? "IPv4" : "IPv6"}</span>
            <span className="text-muted">
              {t(f === "ipv4" ? "floatingIps.ipv4Hint" : "floatingIps.ipv6Hint")}{" "}
              {t("floatingIps.pricePerMonth", {
                price: floatingIpPrice(f, region, prices, i18n.language),
              })}
            </span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

function CreateDialog({
  projectId,
  servers,
  onClose,
}: {
  projectId: string;
  servers: Server[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const regions = useCatalogue(projectId, "regions").data ?? [];
  const prices = useCatalogue(projectId, "prices").data;
  const create = useCreateFloatingIp(projectId);
  const toast = useToast();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [family, setFamily] = useState<IpFamily>("ipv4");
  const [target, setTarget] = useState<"region" | "server">("region");
  const [region, setRegion] = useState("");
  const [serverId, setServerId] = useState("");
  const onServer = target === "server";
  const chosenRegion = onServer
    ? (servers.find((s) => s.id === serverId)?.region ?? regions[0]?.code ?? "")
    : region || regions[0]?.code || "";
  const zones = [...new Set(regions.map((r) => r.zone))];

  const submit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(
      {
        name: name.trim(),
        family,
        description: description.trim(),
        ...(onServer ? { serverId } : { region: chosenRegion }),
      },
      {
        onSuccess: ({ floatingIp }) => {
          toast({ tone: "ok", title: t("floatingIps.created", { address: floatingIp.address }) });
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("floatingIps.create")}
      description={t("floatingIps.createLead")}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form="create-floating-ip"
            busy={create.isPending}
            disabled={onServer && !serverId}
          >
            {t("floatingIps.createSubmit")}
          </Button>
        </>
      }
    >
      <form id="create-floating-ip" onSubmit={submit} className="flex flex-col gap-4">
        <TextField
          label={t("floatingIps.name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={128}
          hint={t("floatingIps.nameHint")}
          error={fieldError(create.error, "name", t)}
        />
        <TextField
          label={t("floatingIps.description")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={255}
          hint={t("floatingIps.descriptionHint")}
        />
        <FloatingFamilyChoice
          family={family}
          onChange={setFamily}
          region={chosenRegion}
          prices={prices}
        />
        <fieldset className="flex flex-col gap-2 text-sm">
          <legend className="mb-1 font-medium">{t("floatingIps.where")}</legend>
          <label className="flex min-h-6 items-center gap-2">
            <input
              type="radio"
              name="target"
              checked={!onServer}
              onChange={() => setTarget("region")}
              className="size-4 accent-accent"
            />
            {t("floatingIps.keepFree")}
          </label>
          <label className="flex min-h-6 items-center gap-2">
            <input
              type="radio"
              name="target"
              checked={onServer}
              onChange={() => setTarget("server")}
              className="size-4 accent-accent"
            />
            {t("floatingIps.onServer")}
          </label>
        </fieldset>
        {onServer ? (
          <ServerSelect
            servers={servers}
            zone={null}
            currentId={null}
            value={serverId}
            onChange={setServerId}
          />
        ) : (
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium">{t("floatingIps.location")}</span>
            <select
              value={chosenRegion}
              onChange={(e) => setRegion(e.target.value)}
              className={selectClass}
            >
              {zones.map((zone) => (
                <optgroup key={zone} label={t("floatingIps.zone", { zone })}>
                  {regions
                    .filter((r) => r.zone === zone)
                    .map((r) => (
                      <option key={r.code} value={r.code}>
                        {r.city} ({r.code})
                      </option>
                    ))}
                </optgroup>
              ))}
            </select>
            <span className="text-xs text-muted">{t("floatingIps.locationHint")}</span>
          </label>
        )}
        {onServer && <Alert tone="neutral">{t("floatingIps.configureNote")}</Alert>}
        <Alert tone="neutral">{t("floatingIps.billing")}</Alert>
        <ErrorMessage error={create.error} />
      </form>
    </Dialog>
  );
}

function AssignDialog({
  projectId,
  ip,
  servers,
  onClose,
}: {
  projectId: string;
  ip: FloatingIp;
  servers: Server[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const assign = useAssignFloatingIp(projectId);
  const toast = useToast();
  const [serverId, setServerId] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    assign.mutate(
      { id: ip.id, body: { serverId } },
      {
        onSuccess: () => {
          toast({ tone: "ok", title: t("floatingIps.assigning", { address: ip.address }) });
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("floatingIps.assignTitle", { address: ip.address })}
      description={t("floatingIps.assignLead", { zone: ip.zone })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form="assign-floating-ip"
            busy={assign.isPending}
            disabled={!serverId}
          >
            {t("floatingIps.assign")}
          </Button>
        </>
      }
    >
      <form id="assign-floating-ip" onSubmit={submit} className="flex flex-col gap-4">
        <ServerSelect
          servers={servers}
          zone={ip.zone}
          currentId={ip.server?.id ?? null}
          value={serverId}
          onChange={setServerId}
        />
        <Alert tone="neutral">{t("floatingIps.configureNote")}</Alert>
        <ErrorMessage error={assign.error} />
      </form>
    </Dialog>
  );
}

/** The commands that make a server accept the address, for the IPv4 itself or an address of the /64. */
export function configureCommands(ip: Pick<FloatingIp, "family" | "address">) {
  const address =
    ip.family === "ipv4" ? `${ip.address}/32` : ip.address.replace(/::\/64$/, "::1/64");
  return {
    now: `sudo ip addr add ${address} dev eth0`,
    netplan: [
      "network:",
      "  version: 2",
      "  ethernets:",
      "    eth0:",
      "      addresses:",
      `        - ${address}`,
    ].join("\n"),
  };
}

function ConfigureDialog({ ip, onClose }: { ip: FloatingIp; onClose: () => void }) {
  const { t } = useTranslation();
  const commands = configureCommands(ip);
  const code = "overflow-x-auto rounded-lg bg-surface-2 px-3 py-2 font-mono text-xs";
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("floatingIps.configureTitle", { address: ip.address })}
      description={t("floatingIps.configureLead")}
      wide
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t("common.close")}
        </Button>
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        <p>{t("floatingIps.configureNow")}</p>
        <pre className={code}>{commands.now}</pre>
        <p>{t("floatingIps.configurePersist")}</p>
        <pre className={code}>{commands.netplan}</pre>
        <p className="text-muted">{t("floatingIps.configureOther")}</p>
      </div>
    </Dialog>
  );
}

function EditDialog({
  projectId,
  ip,
  onClose,
}: {
  projectId: string;
  ip: FloatingIp;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateFloatingIp(projectId);
  const [name, setName] = useState(ip.name);
  const [description, setDescription] = useState(ip.description);
  const [labelsText, setLabelsText] = useState(formatLabels(ip.labels));
  const [badLine, setBadLine] = useState<number | null>(null);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = parseLabels(labelsText);
    setBadLine(parsed.badLine);
    if (!parsed.labels) return;
    update.mutate(
      { id: ip.id, name: name.trim(), description: description.trim(), labels: parsed.labels },
      { onSuccess: onClose },
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("floatingIps.editTitle", { address: ip.address })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="edit-floating-ip" busy={update.isPending}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <form id="edit-floating-ip" onSubmit={submit} className="flex flex-col gap-4">
        <TextField
          label={t("floatingIps.name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={128}
          error={fieldError(update.error, "name", t)}
        />
        <TextField
          label={t("floatingIps.description")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={255}
          error={fieldError(update.error, "description", t)}
        />
        <TextArea
          label={t("floatingIps.labels")}
          value={labelsText}
          onChange={(e) => setLabelsText(e.target.value)}
          rows={3}
          spellCheck={false}
          placeholder="env=production"
          hint={t("floatingIps.labelsHint")}
          error={
            badLine !== null
              ? t("floatingIps.labelsInvalid", { line: badLine })
              : fieldError(update.error, "labels", t)
          }
        />
        <ErrorMessage error={update.error} />
      </form>
    </Dialog>
  );
}

type Open = {
  kind: "assign" | "configure" | "dns" | "edit" | "delete";
  ip: FloatingIp;
} | null;

export function FloatingIpsPage({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const ips = useFloatingIps(projectId);
  const servers = useServers(projectId).data ?? [];
  const prices = useCatalogue(projectId, "prices").data;
  const running = useRunningOperations(projectId);
  const unassign = useUnassignFloatingIp(projectId);
  const protection = useFloatingIpProtection(projectId);
  const reverseDns = useFloatingReverseDns(projectId);
  const remove = useDeleteFloatingIp(projectId);
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<Open>(null);
  const { readOnly, reason: readOnlyReason, token: tokenReadOnly } = useReadOnly(projectId);
  const close = () => setOpen(null);

  const failed = (error: unknown) =>
    toast({
      tone: "danger",
      title:
        error instanceof ApiProblem && error.problem
          ? problemText(t, error.problem)
          : t("common.unexpected"),
    });

  const createButton = (
    <Button
      onClick={() => setCreating(true)}
      disabled={readOnly}
      icon={<Plus className="size-4" aria-hidden />}
    >
      {t("floatingIps.create")}
    </Button>
  );

  const menu = (ip: FloatingIp): MenuItem[][] => {
    const busy = progressOf(running.data, ip.id) !== null;
    const assigned = ip.server !== null;
    const why = (fallback: string) =>
      readOnly ? readOnlyReason : busy ? t("servers.busy") : fallback;
    return [
      [
        {
          label: assigned ? t("floatingIps.move") : t("floatingIps.assign"),
          icon: assigned ? (
            <ArrowRightLeft className="size-4" aria-hidden />
          ) : (
            <Link2 className="size-4" aria-hidden />
          ),
          disabled: readOnly || busy || ip.blocked,
          reason: why(t("floatingIps.why.blocked")),
          onSelect: () => setOpen({ kind: "assign", ip }),
        },
        {
          label: t("floatingIps.unassign"),
          icon: <Unlink className="size-4" aria-hidden />,
          disabled: readOnly || busy || !assigned,
          reason: why(t("floatingIps.why.notAssigned")),
          onSelect: () =>
            unassign.mutate(
              { id: ip.id },
              {
                onSuccess: () =>
                  toast({
                    tone: "ok",
                    title: t("floatingIps.unassigning", { address: ip.address }),
                  }),
                onError: failed,
              },
            ),
        },
        {
          label: t("floatingIps.configure"),
          icon: <Terminal className="size-4" aria-hidden />,
          onSelect: () => setOpen({ kind: "configure", ip }),
        },
      ],
      [
        {
          label: t("floatingIps.dns"),
          icon: <Waypoints className="size-4" aria-hidden />,
          disabled: readOnly || busy,
          reason: why(""),
          onSelect: () => setOpen({ kind: "dns", ip }),
        },
        {
          label: t("floatingIps.edit"),
          icon: <Pencil className="size-4" aria-hidden />,
          disabled: readOnly,
          reason: readOnlyReason,
          onSelect: () => setOpen({ kind: "edit", ip }),
        },
        {
          label: ip.protection.delete ? t("floatingIps.unprotect") : t("floatingIps.protect"),
          icon: ip.protection.delete ? (
            <LockOpen className="size-4" aria-hidden />
          ) : (
            <Lock className="size-4" aria-hidden />
          ),
          disabled: readOnly || busy,
          reason: why(""),
          onSelect: () =>
            protection.mutate(
              { id: ip.id, body: { delete: !ip.protection.delete } },
              { onError: failed },
            ),
        },
      ],
      [
        {
          label: t("floatingIps.delete"),
          icon: <Trash2 className="size-4" aria-hidden />,
          danger: true,
          disabled: readOnly || busy || ip.protection.delete || assigned,
          reason: why(
            ip.protection.delete ? t("floatingIps.why.protected") : t("floatingIps.why.assigned"),
          ),
          onSelect: () => setOpen({ kind: "delete", ip }),
        },
      ],
    ];
  };

  const deleting = open?.kind === "delete" ? open.ip : null;
  return (
    <>
      <PageHeader
        title={t("nav.floatingIps")}
        intro={t("floatingIps.intro")}
        help="floating-ips"
        actions={ips.data?.length ? createButton : undefined}
      />
      {tokenReadOnly && <Alert tone="warn">{readOnlyReason}</Alert>}
      {ips.error ? (
        <ErrorMessage error={ips.error} />
      ) : (
        <DataTable<FloatingIp>
          label={t("nav.floatingIps")}
          rows={ips.data}
          rowKey={(ip) => ip.id}
          initialSort={{ key: "address", dir: "asc" }}
          muted={(ip) => ip.blocked}
          progress={(ip) => progressOf(running.data, ip.id)}
          empty={
            <EmptyState
              icon={<ArrowRightLeft className="size-6" aria-hidden />}
              title={t("floatingIps.empty")}
              action={createButton}
              help="floating-ips"
            >
              {t("floatingIps.emptyLead")}
            </EmptyState>
          }
          columns={[
            {
              key: "address",
              header: t("floatingIps.address"),
              primary: true,
              sort: (ip) => `${ip.family}-${ip.address}`,
              cell: (ip) => (
                <span className="flex min-w-0 flex-col">
                  <span className="whitespace-nowrap font-mono font-medium">{ip.address}</span>
                  <span className="truncate text-xs text-muted" title={ip.description || ip.name}>
                    {ip.name}
                    {ip.description && ` · ${ip.description}`}
                  </span>
                </span>
              ),
            },
            {
              key: "family",
              header: t("floatingIps.family"),
              sort: (ip) => ip.family,
              cell: (ip) => <Badge>{ip.family === "ipv4" ? "IPv4" : "IPv6"}</Badge>,
            },
            {
              key: "region",
              header: t("floatingIps.home"),
              sort: (ip) => ip.region,
              cell: (ip) => (
                <span className="text-muted" title={t("floatingIps.zone", { zone: ip.zone })}>
                  {ip.region}
                </span>
              ),
            },
            {
              key: "server",
              header: t("floatingIps.server"),
              sort: (ip) => ip.server?.name ?? "",
              cell: (ip) =>
                ip.server ? (
                  ip.server.id ? (
                    <Link
                      to="/projects/$projectId/servers/$serverId"
                      params={{ projectId, serverId: ip.server.id }}
                      search={{ tab: "networking" }}
                      className="break-all text-accent hover:underline"
                    >
                      {ip.server.name}
                    </Link>
                  ) : (
                    <span className="break-all">{ip.server.name}</span>
                  )
                ) : (
                  <span className="whitespace-nowrap text-muted">
                    {t("floatingIps.unassigned")}
                  </span>
                ),
            },
            {
              key: "dns",
              header: t("floatingIps.dnsShort"),
              cell: (ip) =>
                ip.reverseDns.length > 0 ? (
                  <span className="break-words text-xs">
                    {ip.reverseDns[0]?.dnsPtr}
                    {ip.reverseDns.length > 1 && ` +${ip.reverseDns.length - 1}`}
                  </span>
                ) : (
                  <span className="text-muted">—</span>
                ),
            },
            {
              key: "flags",
              header: t("floatingIps.flags"),
              cell: (ip) => (
                <span className="flex flex-wrap gap-1">
                  {ip.protection.delete && <Badge tone="ok">{t("floatingIps.protected")}</Badge>}
                  {ip.blocked && <Badge tone="danger">{t("floatingIps.blocked")}</Badge>}
                  {!ip.protection.delete && !ip.blocked && <span className="text-muted">—</span>}
                </span>
              ),
            },
            {
              key: "price",
              header: (
                <span className="inline-flex items-center gap-1">
                  {t("floatingIps.price")}
                  <FieldHint text={t("floatingIps.billing")} topic="floating-ips" />
                </span>
              ),
              align: "right",
              sort: (ip) => (prices ? (floatingIpMonthly(ip, prices) ?? 0) : 0),
              cell: (ip) => (
                <span className="whitespace-nowrap">
                  {floatingIpPrice(ip.family, ip.region, prices, i18n.language)}
                </span>
              ),
            },
            {
              key: "created",
              header: t("floatingIps.createdAt"),
              hideOnPhone: true,
              sort: (ip) => ip.createdAt,
              cell: (ip) => (
                <span className="text-muted">{relativeTime(ip.createdAt, i18n.language)}</span>
              ),
            },
          ]}
          rowMenu={menu}
        />
      )}
      {creating && (
        <CreateDialog projectId={projectId} servers={servers} onClose={() => setCreating(false)} />
      )}
      {open?.kind === "assign" && (
        <AssignDialog projectId={projectId} ip={open.ip} servers={servers} onClose={close} />
      )}
      {open?.kind === "configure" && <ConfigureDialog ip={open.ip} onClose={close} />}
      {open?.kind === "dns" && (
        <ReverseDnsDialog
          projectId={projectId}
          ip={open.ip}
          mutation={reverseDns}
          onClose={close}
        />
      )}
      {open?.kind === "edit" && <EditDialog projectId={projectId} ip={open.ip} onClose={close} />}
      <DangerDialog
        open={deleting !== null}
        onOpenChange={(next) => !next && close()}
        title={t("floatingIps.deleteTitle", { address: deleting?.address ?? "" })}
        consequences={[
          t("floatingIps.deleteLost"),
          ...(deleting?.server
            ? [t("floatingIps.deleteServer", { server: deleting.server.name })]
            : []),
          t("floatingIps.deleteDns"),
          t("floatingIps.deleteBilling"),
        ]}
        confirmText={deleting?.address ?? ""}
        actionLabel={t("floatingIps.delete")}
        busy={remove.isPending}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, {
            onSuccess: () => {
              toast({
                tone: "ok",
                title: t("floatingIps.deleted", { address: deleting.address }),
              });
              close();
            },
            onError: failed,
          })
        }
      />
    </>
  );
}
